import * as React from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { useChargebeeSubscriptions } from '@/features/subscriptions/api/get-chargebee-subscriptions'

type ChargebeeSubscriptionsComboboxProps = {
    name: string
    label?: string
    placeholder?: string
}

export default function ChargebeeSubscriptionsCombobox({ name, label = 'Chargebee Subscription', placeholder = 'Select subscription' }: ChargebeeSubscriptionsComboboxProps) {
    const form = useFormContext()
    const { data, isLoading } = useChargebeeSubscriptions({})
    
    // Ensure this component only sets chargebeeSubscriptionId, never partnerId
    const fieldName = name === 'chargebeeSubscriptionId' ? name : 'chargebeeSubscriptionId'
    const options = React.useMemo(() => {
        const arr = (data as unknown as any[]) ?? []
        return arr.map((s) => {
            const planId = s.planId ?? ''
            const email = s.customer?.email ?? ''
            const searchableText = `${planId} ${email} ${s.subscriptionId}`.toLowerCase()
            return {
                label: <div className='flex flex-col gap-2'><span className='flex items-center gap-2'>
                    <span>{planId}</span> •
                    <span className='font-semibold'> {email}</span>
                </span>
                </div>,
                value: `${s.subscriptionId} ${searchableText}`,
                actualValue: s.subscriptionId,
                extra: s.subscriptionId,
            }
        })
    }, [data])
    const [open, setOpen] = React.useState(false)
    // rely on field.value inside render; no extra memo needed here

    return (
        <FormField
            control={form.control}
            name={fieldName}
            render={({ field }) => (
                <FormItem className='flex flex-col w-full'>
                    {label && <FormLabel>{label}</FormLabel>}
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    variant='outline'
                                    role='combobox'
                                    className={cn('justify-between', !field.value && 'text-muted-foreground')}
                                >
                                    {options.find((o) => o.actualValue === (field.value as string))?.label || (field.value as string) || (isLoading ? 'Loading…' : placeholder)}
                                    <ChevronsUpDown className='opacity-50' />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='p-0'>
                            <Command>
                                <CommandInput placeholder='Search subscriptions…' className='h-9' />
                                <CommandList>
                                    <CommandEmpty>No subscriptions found.</CommandEmpty>
                                    <CommandGroup>
                                        {options.map((opt) => (
                                            <CommandItem
                                                key={opt.actualValue}
                                                value={opt.value}
                                                onSelect={() => {
                                                    // Only set chargebeeSubscriptionId, never partnerId
                                                    form.setValue(fieldName as any, opt.actualValue, { shouldDirty: true })
                                                    setOpen(false)
                                                }}
                                            >
                                                <span className='flex flex-col'>
                                                    <span className='font-medium'>{opt.label}</span>
                                                    {opt.extra && <span className='text-muted-foreground text-xs'>{opt.extra}</span>}
                                                </span>
                                                <Check className={cn('ml-auto', opt.actualValue === field.value ? 'opacity-100' : 'opacity-0')} />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}


